import {FC, useEffect, useRef} from 'react'
import {FormControl, FormLabel} from '@chakra-ui/react'
import {useFormContext} from 'react-hook-form'
import {get} from 'lodash-es'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

import FormErrorMessage from '_app/components/Form/FormErrorMessage'
import {BaseInputProps} from '_app/types/form'
import { isValidPhoneNumber, isPossiblePhoneNumber, parsePhoneNumberFromString, validatePhoneNumberLength } from 'libphonenumber-js/max'
import {useRouter} from '../../../hooks/useRouter'
import {useTranslation} from 'next-i18next'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
import plLocale from 'i18n-iso-countries/langs/pl.json'
import frLocale from 'i18n-iso-countries/langs/fr.json'
import deLocale from 'i18n-iso-countries/langs/de.json'

countries.registerLocale(enLocale)
countries.registerLocale(plLocale)
countries.registerLocale(frLocale)
countries.registerLocale(deLocale)

type Props = Omit<BaseInputProps, 'onChange'>

const PhoneInputComponent: FC<Props> = ({
                                          label,
                                          name,
                                          isRequired,
                                          isDisabled,
                                          mb = 4,
                                          ...props
                                        }) => {
  const {
    setValue,
    getValues,
    clearErrors,
    setError,
    formState: {errors},
  } = useFormContext()
  const { t } = useTranslation('common')
  const inputRef = useRef<HTMLDivElement>(null)
  const { locale }: any = useRouter()
  const errorMessage = get(errors, name)?.message
  const handleChange = (phone: string, data: any) => {
    const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`
    const formattedCountry = data?.countryCode?.toUpperCase()
    const parsedNumber: any = parsePhoneNumberFromString(formattedPhone, formattedCountry) || ''
    setValue(name, formattedPhone)
    if (!parsedNumber || validatePhoneNumberLength(parsedNumber?.number?.toString(), formattedCountry) !== undefined) {
      setError(name, {
        type: 'manual',
        message: t('PHONE_LENGTH'),
      })
    } else if (!parsedNumber || !isPossiblePhoneNumber(formattedPhone.toString(), formattedCountry)) {
      setError(name, {
        type: 'manual',
        message: t('PHONE_NOT_CORRECT'),
      })
    } else if (!parsedNumber || !isValidPhoneNumber(formattedPhone.toString(), formattedCountry)) {
      setError(name, {
        type: 'manual',
        message: t('PHONE_WRONG'),
      })
    } else {
      clearErrors(name)
    }
  }
  useEffect(() => {
    if (errorMessage && inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [errorMessage])
  return (
      <FormControl
          isRequired={isRequired && !isDisabled}
          isInvalid={!!errorMessage}
          mb={mb}
          ref={inputRef}
      >
        <FormLabel htmlFor={name}>{label}</FormLabel>
        <PhoneInput
            {...props}
            country={locale}
            onChange={handleChange}
            value={getValues(name)}
            inputProps={{
              name,
              required: isRequired,
              disabled: isDisabled,
              'data-testid': name,
            }}
            containerStyle={{width: '100%', marginBottom: '8px'}}
            inputStyle={{
              width: 'calc(100% - 54px)',
              height: '40px',
              padding: '8px',
              marginLeft: '54px',
              borderRadius: '0',
              borderTopWidth: '0',
              borderLeftWidth: '0',
              borderRightWidth: '0',
              borderBottom: '1px solid #000',
              outline: 'none',
              backgroundColor: isDisabled ? '#f5f5f5' : '#fff',
              lineHeight: '1.5',
            }}
            buttonStyle={{
              border: 'none',
              backgroundColor: '#f5f5f5',
              padding: '0 8px',
              height: '40px',
            }}
            dropdownStyle={{
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              maxHeight: '200px',
              overflowY: 'auto',
              fontSize: '16px',
            }}
            disabled={isDisabled}
            data-testid={name}
        />
        <FormErrorMessage name={label}>{errorMessage}</FormErrorMessage>
      </FormControl>
  )
}

export default PhoneInputComponent
